import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import React, { useState, createRef, useContext } from "react";

import flex from "../../../styles/flex";
import cssClasses from "./VehicleMovementsScanner.module.css";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import usePaths from "../../../utility/paths";
import BarcodeScanner from "../../../components/thirdParty/react-barcode-reader";
import LocationPickerDialog from "../../../components/pickers/locationPicker/LocationPickerDialog";
import { AssetLocation, AssetLocationTypes, Booking, IVehicleManagerBarcodeScanVehicleMovementRequest, IVehicleManagerBarcodeScanVehicleMovementResponse, RequestStatus, VehicleManagerBarcodeResultType, api } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import BookingListItem from "../../../components/lists/listItems/BookingListItem/BookingListItem";
import { FaCar } from "react-icons/fa";
import { SoundType, useSoundEffects } from "../../../utility/utils";
import { useReleaseManagement, ReleaseFlag } from "../../../utility/feature-management";
import { usePreferences } from "../../../utility/usePreferences";

export const VehicleMovementsScanner: React.FunctionComponent = () => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const history = useHistory();
  const paths = usePaths();
  const playSound = useSoundEffects();

  const isMoveKeyVehicleModalIconsReleased = useReleaseManagement(
    ReleaseFlag.releaseMoveKeyVehicleModalIcons
  );

  const isDisableVehicleScanToUserReleased = useReleaseManagement(
    ReleaseFlag.releaseDisableVehicleScanToUser
  );

  const barcodeScanVehicleMovement = api.hooks.vehicleManager.barcodeScanVehicleMovement();

  const [selectedLocation, setSelectedLocation] = useState<AssetLocation | null>(null);
  const [showLocationPicker, setShowLocationPicker] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<{ message: string, level: string }[]>([]);
  const [scanned, setScanned] = useState<Booking[]>([]);
  const inProgress = barcodeScanVehicleMovement.status === RequestStatus.loading;

  const inputRef = createRef<HTMLInputElement>();

  const { workflowRules } = usePreferences();

  const switchLocation = (location: AssetLocation) => {
    if (location.id === selectedLocation?.id) {
      setFeedback([
        {
          message: t("errors.locationAlreadySet", { location: location.name }),
          level: "error",
        }
      ]);
      playSound(SoundType.Bad);
      return;
    }


    if (isDisableVehicleScanToUserReleased && workflowRules?.preventVehicleScanToUser && location?.type === AssetLocationTypes.User) {
      setFeedback([
        {
          message: t("errors.cannotMoveToUser"),
          level: "error",
        }
      ]);
      playSound(SoundType.Bad);
      return;
    }

    setFeedback([{ message: t("messages.locationChanged", { location: location.name }), level: "ok" }]);
    playSound(SoundType.Swap);
    setSelectedLocation(location);
    setScanned([]);
  }

  const onLocationPickerChange = (loc: AssetLocation | null) => {
    setFeedback([]);
    if (loc) {
      switchLocation(loc);
    }
    setShowLocationPicker(false);
  }

  const onBarcodeScanComplete = (data: IVehicleManagerBarcodeScanVehicleMovementResponse | null) => {
    if (!data) {
      return;
    }

    if (!data.result?.success) {
      let displayMessage = "";
      if (data.type === VehicleManagerBarcodeResultType.invalidInput) {
        displayMessage = data.result.message.text;
      } else {
        const message = t("errors.vehicleMovementBlockedLocation", { ref: data.booking?.ref, location: selectedLocation?.name });
        const warning = data.result.message.text ? ` (${data.result.message.text})` : "";
        displayMessage = `${message}${warning}`;
      }
      setFeedback([{ message: displayMessage, level: data.result.message.level }]);
      playSound(SoundType.Bad);
      return;
    }

    if (data.location) {
      switchLocation(data.location);
      return;
    }

    if (data.booking) {
      setScanned([data.booking, ...scanned]);
      const message = t("messages.vehicleMoved", { ref: data.booking.ref, location: selectedLocation?.name });
      const warning = data.result.message.text ? ` (${data.result.message.text})` : "";
      setFeedback([{ message: `${message}${warning}`, level: data.result.message.level }]);
      playSound(data.result.message.level === "ok" ? SoundType.Good : SoundType.Warning);
    }
  }

  const onBarcodeScanned = (scan: string) => {
    setFeedback([]);

    if (inputRef.current) {
      inputRef.current.value = "";
    }

    if (inProgress) {
      return;
    }

    const trimmedScan = (scan ?? '').trim();
    if (trimmedScan.length === 0) {
      return;
    }

    const scanParameters: IVehicleManagerBarcodeScanVehicleMovementRequest = {
      scan: trimmedScan,
      locationId: selectedLocation?.id || null,
      scanDate: new Date().toISOString(),
    };

    barcodeScanVehicleMovement.send(
      session.current.context,
      scanParameters,
      onBarcodeScanComplete
    );
  };

  return <>
    <LocationPickerDialog
      title={t("keys.labels.selectLocation")}
      show={showLocationPicker}
      displayTypes={[AssetLocationTypes.Zone, AssetLocationTypes.Lane]}
      selectionTypes={[AssetLocationTypes.Lane]}
      onChange={onLocationPickerChange}
    >
    </LocationPickerDialog>

    <Dialog maxWidth={"md"} open={true}>
      <DialogTitle>
        <div className={cssClasses.title__main}>
          {isMoveKeyVehicleModalIconsReleased ? (<FaCar size={100} />) : null}
          <span>{t("vehicles.labels.vehicleMovements")}</span>
        </div>

        <div className={cssClasses.title__location}>
          {(!selectedLocation) ? (
            <span>{t("keys.labels.scanLocation")}</span>
          ) : (
            <b>{selectedLocation.name}</b>
          )}
        </div>

        {selectedLocation != null && (
          <div className={cssClasses.title__scanCount}>
            {t("vehicles.labels.vehiclesScanned", { count: scanned.length })}
          </div>
        )}
      </DialogTitle>

      <DialogContent style={{ minHeight: 50, width: 800 }}>
        <div id="location-selection">
          <div style={flex.flexRows}>
            <div style={flex.flexFill}>
              <BarcodeScanner
                onError={(err: string) => console.log(err)}
                onScan={onBarcodeScanned}
                minLength={1}
              />

              <TextField
                focused={true}
                inputProps={{
                  autoComplete: "off",
                  name: "input",
                }}
                style={{
                  width: "100%",
                }}
                inputRef={inputRef}
                type={"search"}
                size="small"
                label={t("expressCheckout.labels.barcode")}
                variant="outlined"
                onKeyDown={(e) => {
                  if (e.key == "Enter") {
                    onBarcodeScanned(inputRef.current?.value || "");
                  }
                }}
              />
            </div>

            <div style={flex.flexAuto}>
              <div style={{ marginLeft: 10 }}>
                <Button onClick={() => setShowLocationPicker(true)}
                  variant="contained"
                  color={selectedLocation == null ? "secondary" : "primary"}>
                  {selectedLocation == null ? (<>{t("keys.labels.selectLocation")}</>) : (<>{t("keys.labels.changeLocation")}</>)}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ProgressDialog status={barcodeScanVehicleMovement.status} />

        <div id="feedback" style={{ marginTop: 24 }}>
          {feedback.map((feedback, index) => {
            return <Feedback key={index} message={feedback.message} level={feedback.level} />
          })}
        </div>

        <div style={{ minHeight: 400 }}>
          {scanned.map((booking) => (
            <BookingListItem
              booking={booking}
              key={booking.uuid}
              onClick={() => window.open(paths.booking(booking.id))}
            />
          ))}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => history.push(paths.vehicleManager)}>
          <b>{t("general.done")}</b>
        </Button>
      </DialogActions>
    </Dialog>
  </>
}

interface IFeedbackProps {
  message: string;
  level: string;
}

const Feedback = (props: IFeedbackProps) => {
  const { message, level } = props;
  const levelClassName = `feedback-${level}`;

  return <div className={`${cssClasses.feedback} ${cssClasses[levelClassName]}`}>
    {message}
  </div>
}
